/**
 * `<RequireSuperadmin>` — route-level gate for `/admin/*` (#429 Wave 2,
 * Copilot review on PR #521).
 *
 * The sidebar entry is hidden for non-superadmins by
 * `useCanAccessAdmin()`, but a non-superadmin can still deep-link
 * directly to `/admin/<anything>` and reach the admin shell. This
 * component is the second layer — when used as the `element` of a
 * `<Route>`, it renders `<Outlet />` for superadmins and a 403
 * stub for everyone else.
 *
 * Mirrors `IdentityGate`'s shape (loading / error / forbidden /
 * allowed) so the failure modes are familiar. Note that the gate
 * sits BELOW IdentityGate in the route tree — by the time this
 * component runs, identity is already `ready`, so the only branches
 * we actually exercise are forbidden (non-superadmin) and allowed
 * (superadmin). The loading/error fallbacks exist as defense in
 * depth in case someone reuses this component outside `IdentityGate`
 * in the future.
 *
 * Fail-closed: an absent identity is treated as forbidden, not
 * allowed. Server-side `requireSuperadmin` is the actual security
 * boundary; this client gate is for UX + defense in depth.
 *
 * Third branch — escalation gate (#1531). A superadmin holds their
 * flag forever but operates with powers OFF until they flip the
 * per-session `x-superadmin-mode` toggle on (NIST AC-6(2)). EVERY
 * `/api/admin/*` route requires that toggle, so with it off every
 * admin page's data load 403s — a dead shell that, before this gate,
 * dumped the raw "set x-superadmin-mode: on" error onto the screen.
 * Rather than let each page render that, this wrapper shows one clear
 * "Turn on Global Admin mode" card for the whole `/admin/*` tree.
 * Generic by construction: it sits above every admin page, so the
 * gate is identical everywhere — not Analytics-specific.
 */
import { Outlet } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { Button } from '@/components/ui'
import { useCanAccessAdmin } from '@/hooks/useCanAccessAdmin'
import { useSuperadminMode } from '@/hooks/useSuperadminMode'
import { setSuperadminMode } from '@/auth/superadminMode'
import { useIdentity } from '@/hooks/useIdentity'

export function RequireSuperadmin() {
  const { state } = useIdentity()
  const canAccess = useCanAccessAdmin()
  const superadminMode = useSuperadminMode()

  if (state.status === 'loading') {
    return (
      <div className="min-h-dvh flex items-center justify-center text-muted-foreground text-sm">
        Loading…
      </div>
    )
  }

  if (!canAccess) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center gap-2 px-4 text-center">
        <h1 className="text-lg font-semibold">Admin access required</h1>
        <p className="text-sm text-muted-foreground max-w-md">
          This area is reserved for OttoTester admins. If you
          believe you should have access, contact a platform admin.
        </p>
      </div>
    )
  }

  // Superadmin, but their per-session escalation is OFF. Every
  // /api/admin/* route requires it, so without it every admin page's
  // data load 403s. Show one clear gate that turns it on in a single
  // click instead of a dead page. Lives in this whole-/admin/* wrapper
  // so it's generic across every admin page, not per-page. (#1531)
  if (!superadminMode) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center gap-3 px-4 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <Shield className="h-6 w-6" />
        </div>
        <h1 className="text-lg font-semibold">Turn on Global Admin mode</h1>
        <p className="text-sm text-muted-foreground max-w-md">
          Admin tools run with elevated access that’s turned off by
          default. Turn it on for this session to open the admin console.
        </p>
        <Button
          variant="destructive"
          onClick={() => setSuperadminMode(true)}
          className="gap-2"
        >
          <Shield className="h-4 w-4" />
          Turn on Global Admin mode
        </Button>
        <p className="text-xs text-muted-foreground">
          While it’s on, your access to admin tools is recorded in the
          Audit Log.
        </p>
      </div>
    )
  }

  return <Outlet />
}
